import React from 'react';
import { gql, TSFixMe, getCourseType } from '@yi/core';

import { graphql } from 'gatsby';
import { utilities, getCourseTypeLabel } from '~/common';
import { isRecentlyPublished } from '~/src/common/content';
import { useCourseSale } from '~/src/common/courses';
import { useCurrentUser } from '~/src/common/user';
import { isNotNull, ReplaceProps } from '~/types';

import { CourseAccessTip } from './CourseAccessTip';
import { ContentTitle, LayersOutlined } from './elements';
import { OnSaleRibbon } from './OnSaleRibbon';
import {
  Avatar,
  AccessTime,
  AuthorNameLight,
  DurationIconButton,
  DurationLight,
} from '~/src/components/ClassIconComponents';
import { LazyLoadWrapper } from '~/src/components/LazyLoadWrapper';
import { useLocale } from '~/src/components/LocaleProvider';

import { BaseCard, BaseCardProps } from '../bases';
import { CardNewChip } from '../CardNewChip';
import { Author, BorderLinearProgress, ProgressBarWrapper } from '../elements';

type Item = ReplaceProps<gql.typesClient.Fragment_CourseCard, { access_type: TSFixMe }>;
interface Props extends Omit<Partial<BaseCardProps>, 'onClick'> {
  item: Item;
  noHover?: boolean;
  onClick?(i: Item): void;
}
export type CourseCardPropsType = Props;

const getCoursePrice = (
  course: Item,
  isPurchased: boolean,
  memberTier?: gql.typesClient.MembershipTier,
) => {
  if (isPurchased || (memberTier && course.permissions?.includes(memberTier))) return '';
  return course.a_la_carte_price ? `$${course.a_la_carte_price}` : null;
};

const UpperLeft: React.FC<{
  memberTier?: gql.typesClient.MembershipTier;
  course: Item;
  isPurchased: boolean;
  courseType: gql.typesClient.CourseType;
}> = ({ memberTier, course, isPurchased, courseType }) => {
  const { locale } = useLocale();
  const courseLabel = { icon: <LayersOutlined />, text: getCourseTypeLabel(courseType, locale) };
  const courseSale = useCourseSale(course);
  const isPriceStrikethrough = courseSale.isOnSale();
  const coursePrice = getCoursePrice(course, isPurchased, memberTier);
  const isNew = isRecentlyPublished(course);
  return isNew ? (
    <CardNewChip />
  ) : (
    <ContentTitle>
      {courseLabel.icon}
      <span>
        {courseLabel.text}
        {coursePrice && (
          <>
            {` | `}
            <span style={{ textDecoration: isPriceStrikethrough ? 'line-through' : '' }}>
              {coursePrice}
            </span>
          </>
        )}
      </span>
    </ContentTitle>
  );
};

const dimension = {
  width: 320,
  height: 320,
};

export const CourseCard: React.FC<Props> & {
  dimension: { width: number; height: number };
} = props => {
  const { item, noHover = true, onClick = () => undefined, ...restProps } = props;
  const { id, localized_path, teachers, square_image, course_type } = item;
  const thumb = square_image?.processed_url || '';
  const thumbLabel = `"${item.title}" course card background image`;
  const teacher = teachers?.filter(isNotNull)[0];
  const teacherUrl = teacher?.image?.processed_url || '';
  const imageHeight = 194;
  const courseType = getCourseType(item);
  const isCommunityCourse = courseType === 'community';
  // Truncate if we're dealing with a community course
  const teacherName =
    (teacher &&
      (isCommunityCourse ? utilities.truncate(teacher.full_name, 40) : teacher.full_name)) ||
    '';
  const { currentUser } = useCurrentUser();
  const inProgressCourse = currentUser?.in_progress_courses?.find(c => c.course.id === id);
  const percentComplete = inProgressCourse ? inProgressCourse.completion_percent : 0;

  return (
    <BaseCard
      onClick={() => {
        onClick(item);
      }}
      dimension={dimension}
      hoverable={!noHover}
      url={localized_path}
      thumb={thumb}
      thumbLabel={thumbLabel}
      imageHeight={imageHeight}
      bgImageFull
      renderImageOverlay={() => <OnSaleRibbon courseDetails={item} />}
      renderUpperLeft={() => (
        <UpperLeft
          memberTier={currentUser?.membership_tier}
          course={item}
          courseType={course_type}
          isPurchased={!!currentUser?.purchased_courses?.find(c => c.course.id === id)}
        />
      )}
      renderUpperRight={() => (
        <CourseAccessTip
          currentUser={currentUser}
          course={item}
          price={item.a_la_carte_price}
          categories={item.categories}
          permissions={item.permissions}
        />
      )}
      renderLowerLeft={() => (
        <Author>
          <LazyLoadWrapper enabled={restProps.lazyLoad} width={32} height={24}>
            <Avatar imgProps={{ role: 'presentation' }} src={teacherUrl}>
              Y
            </Avatar>
          </LazyLoadWrapper>
          <AuthorNameLight>{teacherName}</AuthorNameLight>
        </Author>
      )}
      renderLowerRight={() =>
        courseType === 'course' && (
          <DurationIconButton aria-label="Settings">
            <AccessTime />
            <DurationLight>{item?.duration?.text}</DurationLight>
          </DurationIconButton>
        )
      }
      renderProgressBarInFooter={() => (
        <ProgressBarWrapper>
          <BorderLinearProgress variant="determinate" color="primary" value={percentComplete} />
        </ProgressBarWrapper>
      )}
      {...restProps}
    />
  );
};

CourseCard.dimension = dimension;

export const fragmentCourseCard = graphql`
  fragment Fragment_CourseCard on YI_Course {
    localized_path
    description
    action_type
    id
    duration {
      text
    }
    title
    teachers {
      id
      full_name
      image(params: { width: 50, format: webp }) {
        processed_url
      }
    }
    access_type
    action_type
    ecourse_price
    a_la_carte_price
    course_type
    permissions
    square_image(params: { width: 560, format: webp }) {
      processed_url
    }
    upcoming_until {
      yyyymmdd
    }
    categories {
      id
      name
    }
    sale {
      value {
        amount
        type
      }
      coupon_code
    }
    entry_date {
      yyyymmdd
    }
  }
`;
